import { DeleteFilled, DislikeTwoTone, LikeTwoTone, SafetyCertificateTwoTone } from "@ant-design/icons";
import { faCircleExclamation, faCircleQuestion, faClockRotateLeft, faCrosshairs, faEnvelope, faMapLocationDot, faMapPin, faRoad } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Space, Tooltip, Typography } from "antd";
import { resolveLocation } from "common/locationResolver";
import { DATA_RECORD_ANNOTATION_TYPE } from "common/recordDiscussions";
import DataObjectLink from "components/atoms/DataObjectLink";
import FormDisplayField from "components/atoms/FormDisplayField";
import LoadingOverlay from "components/atoms/LoadingOverlay";
import SchemaFieldTitle from "components/atoms/SchemaFieldTitle";
import SlideMenuGuide, { useSlideMenuGuide } from "components/atoms/SlideMenuGuide";
import SlideoutMenu from "components/atoms/SlideoutMenu";
import SubsumedBadge, { subsumedInto } from "components/atoms/SubsumedBadge";
import RecordDiscussionIndicators from "components/molecules/RecordDiscussionIndicators";
import DataObjectShareOption from "containers/DataObjectShareOption";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDataObjectByUUIDRequest } from "store/dataObjects/actions";
import { getRecordDiscussionSummaryRequest } from "store/recordDiscussions/actions";
import CypressTestIds from "../../../cypress/CypressTestIds";
import DataTable from "../DataTable";

const { Text } = Typography;

interface IDataObjectsPreviewListProps {
    showRecordCount?: boolean;
    schemaFields: ISchemaField[];
    dataObjectList: IDataObject[];
    loading?: boolean;
    onPageSelect: (pagination: IPagination) => void;
    onDataObjectSelected: (data: IDataObject) => void;
    onShowDataObjectHistory: (dataObjectId: string) => void;
    onDeleteDataObject: (id: string) => void;
    recordCount: number;
    selectedDataObject?: IDataObject;
    onOpenLocation?: (data: IDataObject) => void;
    showDiscussionIndicators?: boolean;
    annotationRecordType?: string;
    onOpenComments?: (data: IDataObject) => void;
    onOpenAnnotations?: (data: IDataObject) => void;
    onVerifyEmail?: (data: IDataObject) => void;
}

const DataObjectsPreviewList = ({
    selectedDataObject,
    showRecordCount = true,
    recordCount,
    schemaFields,
    dataObjectList,
    loading,
    onPageSelect,
    onDataObjectSelected,
    onShowDataObjectHistory,
    onDeleteDataObject,
    onOpenLocation,
    showDiscussionIndicators = false,
    annotationRecordType = DATA_RECORD_ANNOTATION_TYPE,
    onOpenComments,
    onOpenAnnotations,
    onVerifyEmail,
}: IDataObjectsPreviewListProps) => {
    const dispatch = useDispatch();
    const aiHighlightedDataObjectIds = useSelector((s: any) => s.dataObjects?.aiHighlightedDataObjectIds || []) as string[];
    const aiHighlightedSet = useMemo(() => new Set(aiHighlightedDataObjectIds), [aiHighlightedDataObjectIds]);
    const canViewComments = useSelector((s: any) => !!s.comments?.userCommentsPermission?.canView);
    const discussionIndicatorsEnabled = showDiscussionIndicators && canViewComments;
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [pagination, setPagination] = useState<{ defaultPageSize: number; pageSize: number; page: number }>({ defaultPageSize: 100, pageSize: 100, page: 1 });
    const [showMenuGuide, setShowMenuGuide] = useState(false);
    const { isReady, guidePostion } = useSlideMenuGuide("ant-table-header", "ant-table-row");

    useEffect(() => {
        setShowMenuGuide(isReady);
    }, [isReady]);

    useEffect(() => {
        if (!selectedDataObject) {
            // Unselection detected, should clear selectedRowKeys
            setSelectedRowKeys([]);
        }
    }, [selectedDataObject]);

    const paginatedDataObjectList = useMemo(
        () => (dataObjectList ? dataObjectList.slice((pagination.page - 1) * pagination.pageSize, pagination.page * pagination.pageSize) : []),
        [dataObjectList, pagination.page, pagination.pageSize],
    );

    useEffect(() => {
        if (!discussionIndicatorsEnabled || paginatedDataObjectList.length === 0) {
            return;
        }
        const recordIds = paginatedDataObjectList.map((dataObject) => dataObject.dataObjectId).filter(Boolean);
        if (recordIds.length > 0) {
            dispatch(getRecordDiscussionSummaryRequest(annotationRecordType, recordIds));
        }
    }, [discussionIndicatorsEnabled, paginatedDataObjectList, annotationRecordType, dispatch]);

    const handleSelectDataObject = useCallback(
        (record: IDataObject) => {
            setSelectedRowKeys([record.dataObjectId]);
            try {
                dispatch(getDataObjectByUUIDRequest(record.dataObjectId));
            } catch (e) {
                // Shouldn't have empty block.
                console.error(e);
            }
            const foundDataObject = dataObjectList.find((dataObject) => dataObject.dataObjectId === record.dataObjectId);
            if (onDataObjectSelected && foundDataObject) {
                onDataObjectSelected(foundDataObject);
            }
        },
        [onDataObjectSelected, dataObjectList, dispatch],
    );

    const onClickRow = useCallback(
        (record: IDataObject) => {
            return {
                onClick: () => {
                    handleSelectDataObject(record);
                },
            };
        },
        [handleSelectDataObject],
    );

    const handleOpenLocation = useCallback(
        (dataObject: IDataObject) => {
            setSelectedRowKeys([dataObject.dataObjectId]);
            try {
                dispatch(getDataObjectByUUIDRequest(dataObject.dataObjectId));
            } catch (e) {
                console.error(e);
            }
            const foundDataObject = dataObjectList.find((d) => d.dataObjectId === dataObject.dataObjectId) || dataObject;
            if (onOpenLocation) {
                onOpenLocation(foundDataObject);
            } else if (onDataObjectSelected && foundDataObject) {
                onDataObjectSelected(foundDataObject);
            }
        },
        [dataObjectList, dispatch, onDataObjectSelected, onOpenLocation],
    );

    const onPageClick = useCallback(
        (page: number, pageSize: number) => {
            let currentPagination = { ...pagination };
            currentPagination.page = page;
            currentPagination.pageSize = pageSize;
            setPagination(currentPagination);
            onPageSelect && onPageSelect({ current: page, pageSize });
        },
        [onPageSelect, pagination],
    );

    const renderAddressQuality = (dataObject: IDataObject) => {
        try {
            const meta = dataObject._system_geodata as any;
            if (meta && meta.qualityScore) {
                const q = meta.qualityScore;
                if (q === "ROOFTOP") {
                    return (
                        <Tooltip title={meta.isPartialMatch ? "Exact address match (partial correction applied)" : "Exact address match"}>
                            <FontAwesomeIcon icon={faMapLocationDot} style={{ color: "#16a34a", fontSize: 14 }} />
                        </Tooltip>
                    );
                }
                if (q === "RANGE_INTERPOLATED") {
                    return (
                        <Tooltip title="Street range (approximate) — less precise than exact address">
                            <FontAwesomeIcon icon={faRoad} style={{ color: "#d97706", fontSize: 14 }} />
                        </Tooltip>
                    );
                }
                if (q === "GEOMETRIC_CENTER" || q === "APPROXIMATE") {
                    return (
                        <Tooltip title={q === "GEOMETRIC_CENTER" ? "Area centroid (area-level match, lower precision)" : "Broad region match (low precision)"}>
                            <FontAwesomeIcon icon={faCircleQuestion} style={{ color: "#dc2626", fontSize: 14 }} />
                        </Tooltip>
                    );
                }
                if (q === "EXACT_COORDINATES") {
                    return (
                        <Tooltip title="Manual coordinates (user-specified pin)">
                            <FontAwesomeIcon icon={faCrosshairs} style={{ color: "#2563eb", fontSize: 14 }} />
                        </Tooltip>
                    );
                }
            }

            const resolved = resolveLocation(schemaFields || [], dataObject || ({} as any));
            if (resolved && resolved.type) {
                return (
                    <Tooltip title={"Address fields detected — click to open Location panel to geocode"}>
                        <Button
                            type="text"
                            icon={<FontAwesomeIcon icon={faMapPin} style={{ fontSize: 14, color: "#6b7280" }} />}
                            // className="m-0 border-0 bg-transparent p-0 "
                            style={{
                                height: "14px",
                                width: "14px",
                                lineHeight: 1,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: "unset", // AntD sets a default min-width
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenLocation(dataObject);
                            }}
                        />
                    </Tooltip>
                );
            }

            if (resolved && resolved.error) {
                const code = resolved.error.code;
                const tip = resolved.error.message;
                if (code === "MASKED_DATA") {
                    return (
                        <Tooltip title={tip}>
                            <FontAwesomeIcon icon={faCircleExclamation} style={{ color: "#6b7280", fontSize: 14 }} />
                        </Tooltip>
                    );
                }
                if (code === "EMPTY_VALUE" || code === "INSUFFICIENT_PARTS") {
                    return (
                        <Tooltip title={tip}>
                            <FontAwesomeIcon icon={faCircleExclamation} style={{ color: "#d97706", fontSize: 14 }} />
                        </Tooltip>
                    );
                }
                if (code === "INVALID_COORDINATES" || code === "NULL_ISLAND") {
                    return (
                        <Tooltip title={tip}>
                            <FontAwesomeIcon icon={faCircleQuestion} style={{ color: "#dc2626", fontSize: 14 }} />
                        </Tooltip>
                    );
                }
            }
        } catch (e) {
            // swallow render errors
        }
        return null;
    };

    // Per-record trigger for deep (magic-link) email verification. Mirrors the address
    // map-pin: an envelope button in the status column that opens the record's
    // Verification side-panel tab (the existing consent/magic-link flow). The passive
    // domain-reachability badge (from the async batch job) is rendered separately by
    // EmailVerificationBadge; this button is the on-demand deep verification action.
    const renderEmailVerification = (dataObject: IDataObject) => {
        try {
            if (!onVerifyEmail) {
                return null;
            }
            const emailField = (schemaFields || []).find((f: any) => {
                const isEmailType = f.dataType === "email";
                const hasEmailClassifier =
                    Array.isArray(f.classifiers) && f.classifiers.some((c: any) => c.tagName && c.tagName.toLowerCase() === "email");
                return isEmailType || hasEmailClassifier;
            });
            if (!emailField) {
                return null;
            }
            const value = (dataObject as any)[emailField.fieldId];
            if (!value || typeof value !== "string" || !value.includes("@")) {
                return null;
            }
            const alreadyVerified = (dataObject as any)._verified === true;
            const tip = alreadyVerified ? "Email verified — click to re-send verification link" : "Verify this email (send a verification link)";
            return (
                <Tooltip title={tip}>
                    <Button
                        type="text"
                        icon={<FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 14, color: alreadyVerified ? "#16a34a" : "#6b7280" }} />}
                        style={{
                            height: "14px",
                            width: "14px",
                            lineHeight: 1,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "unset",
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            const found = dataObjectList.find((d) => d.dataObjectId === dataObject.dataObjectId) || dataObject;
                            onVerifyEmail(found);
                        }}
                    />
                </Tooltip>
            );
        } catch (e) {
            // swallow render errors
        }
        return null;
    };

    return loading ? (
        <div className="flex size-full flex-1 items-center justify-center">
            <LoadingOverlay busy message="A moment please..." spinner />
        </div>
    ) : (
        <DataTable<IDataObject>
            dataTestId={CypressTestIds.DATASET_RECORDS_TABLE}
            fixedHeader
            hideSelectionColumn
            showFooter={showRecordCount}
            footer={
                <Space orientation="horizontal">
                    <Text type="secondary">Number of records: </Text>
                    <Text type="success">{recordCount || 0}</Text>
                </Space>
            }
            tableProps={{
                dataSource: paginatedDataObjectList,
                size: "small",
                rowKey: "dataObjectId",
                className: "data-objects-preview-table",
                onRow: onClickRow,
                rowClassName: (record: IDataObject) => (aiHighlightedSet.has(record.dataObjectId) ? "cursor-pointer ai-assistant-highlight" : "cursor-pointer"),
                rowSelection: {
                    selectedRowKeys,
                },
                pagination: {
                    total: (dataObjectList && dataObjectList.length) || 0,
                    defaultPageSize: pagination.defaultPageSize,
                    pageSize: pagination.pageSize,
                    current: pagination.page,
                    onChange: onPageClick,
                },
                columns: [
                    {
                        fixed: "left",
                        width: "10px",
                        key: "actions",
                        className: "overflow-visible !p-1",
                        shouldCellUpdate: (record: IDataObject, prevRecord: IDataObject) => {
                            return record !== prevRecord;
                        },
                        render: (_value: any, dataObject: IDataObject) => (
                            <SlideoutMenu>
                                {!!onShowDataObjectHistory && (
                                    <Tooltip title="Data Record History">
                                        <Button
                                            type="link"
                                            icon={<FontAwesomeIcon icon={faClockRotateLeft} />}
                                            onClick={() => onShowDataObjectHistory(dataObject.dataObjectId)}
                                            aria-label="Data Record History"
                                        />
                                    </Tooltip>
                                )}
                                <div className="block h-6 w-0 border-r border-dashed border-gray-300" />
                                <Button
                                    danger
                                    type="text"
                                    aria-label="Remove Record"
                                    className="action-cursor"
                                    onClick={() => onDeleteDataObject(dataObject.dataObjectId)}
                                    icon={<DeleteFilled />}
                                />
                            </SlideoutMenu>
                        ),
                    },
                    {
                        width: discussionIndicatorsEnabled ? "110px" : "40px",
                        key: "status",
                        shouldCellUpdate: (record: IDataObject, prevRecord: IDataObject) => {
                            return record !== prevRecord;
                        },
                        render: (_value: any, dataObject: IDataObject) => (
                            <Space size={"small"}>
                                {!dataObject._verificationId ? (
                                    <>
                                        <Tooltip title={"Verification not initiated"}>
                                            <SafetyCertificateTwoTone twoToneColor={"#aeb0ac"} />
                                        </Tooltip>
                                        <Tooltip title={"Consent not requested"}>
                                            <LikeTwoTone twoToneColor={"#aeb0ac"} />
                                        </Tooltip>
                                    </>
                                ) : dataObject._verificatioExpiry && moment().utc().isAfter(moment(dataObject._verificatioExpiry).utc()) ? (
                                    <>
                                        <Tooltip title={"Verification link expired"}>
                                            <SafetyCertificateTwoTone twoToneColor={"red"} />
                                        </Tooltip>
                                        <Tooltip title={"Consent not requested"}>
                                            <LikeTwoTone twoToneColor={"#aeb0ac"} />
                                        </Tooltip>
                                    </>
                                ) : !dataObject._verified ? (
                                    <>
                                        <Tooltip title={"Verification link sent"}>
                                            <SafetyCertificateTwoTone />
                                        </Tooltip>
                                        <Tooltip title={"Consent not requested"}>
                                            <LikeTwoTone twoToneColor={"#aeb0ac"} />
                                        </Tooltip>
                                    </>
                                ) : (
                                    <>
                                        <Tooltip title={"Verified"}>
                                            <SafetyCertificateTwoTone twoToneColor={"#78d11f"} />
                                        </Tooltip>
                                        {dataObject._consent === false ? (
                                            <Tooltip title={"Consent not granted or revoked"}>
                                                <DislikeTwoTone twoToneColor={"red"} />
                                            </Tooltip>
                                        ) : dataObject._consent === true ? (
                                            <Tooltip title={"Consent granted"}>
                                                <LikeTwoTone twoToneColor={"#78d11f"} />
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title={"Consent not requested"}>
                                                <LikeTwoTone twoToneColor={"#aeb0ac"} />
                                            </Tooltip>
                                        )}
                                    </>
                                )}
                                {dataObject.errors && dataObject.errors.length > 0 && (
                                    <Tooltip placement="topLeft" title={`Record contains ${dataObject.errors.length} error(s)`}>
                                        <span className="flex size-[18px] items-center justify-center rounded-full border border-red-500 bg-red-50 text-[12px] text-red-500">
                                            {dataObject.errors.length}
                                        </span>
                                    </Tooltip>
                                )}

                                {/* Inline address-quality indicator merged into the status column */}
                                {renderAddressQuality(dataObject)}

                                {discussionIndicatorsEnabled && (
                                    <RecordDiscussionIndicators
                                        recordId={dataObject.dataObjectId}
                                        recordType={annotationRecordType}
                                        onOpenComments={onOpenComments ? () => onOpenComments(dataObject) : undefined}
                                        onOpenAnnotations={onOpenAnnotations ? () => onOpenAnnotations(dataObject) : undefined}
                                    />
                                )}
                                {/* Inline deep email-verification (magic-link) trigger */}
                                {renderEmailVerification(dataObject)}
                            </Space>
                        ),
                    },

                    {
                        width: "40px",
                        title: "No.",
                        key: "index",
                        shouldCellUpdate: (record: IDataObject, prevRecord: IDataObject) => {
                            return record !== prevRecord;
                        },
                        render: (_value: any, item: IDataObject, index: number) => item.sNo || index + 1,
                    },
                    {
                        width: "120px",
                        title: "Record Id",
                        key: "RecordId",
                        ellipsis: {
                            showTitle: false,
                        },
                        shouldCellUpdate: (record: IDataObject, prevRecord: IDataObject) => {
                            return record !== prevRecord;
                        },
                        onCell: (_record: IDataObject) => ({
                            style: { cursor: "default" }, // Minor user guide to indicate that only the link is clickable, not the whole cell.
                            onClick: (e) => {
                                // Prevent this whole column from activating the row click.
                                e.stopPropagation && e.stopPropagation();
                            },
                        }),
                        render: (_value: any, dataObject: IDataObject, _index: number) => {
                            return (
                                <DataObjectShareOption
                                    key={dataObject.dataObjectId}
                                    dataObjectId={dataObject.dataObjectId}
                                    dataSetId={dataObject.dataSetId}
                                    schemaId={dataObject.schemaId}
                                    businessAreaId={dataObject.businessAreaId}
                                >
                                    <DataObjectLink dataObjectId={dataObject.dataObjectId}></DataObjectLink>
                                    {subsumedInto(dataObject) !== null && <SubsumedBadge className="ml-2" survivorId={subsumedInto(dataObject)} />}
                                </DataObjectShareOption>
                            );
                        },
                    },
                    ...((schemaFields &&
                        schemaFields.map((field) => ({
                            key: field.fieldId,
                            dataIndex: field.fieldId,
                            shouldCellUpdate: (record: IDataObject, prevRecord: IDataObject) => {
                                return record !== prevRecord;
                            },
                            title: <SchemaFieldTitle name={field.name} tags={field.classifiers} isPrimaryKey={field.isPrimaryKey}></SchemaFieldTitle>,
                            render: (value: any, dataObject: IDataObject, index: number) => {
                                let displayValue = value;
                                let fieldError = dataObject.errors?.find((error: { name: string; errors: string }) => error.name === field.fieldId)?.errors;
                                return field.isPII ? (
                                    <Tooltip placement="topLeft" title="This value is masked to protect sensitive information.">
                                        <div className="bg-gray-100 p-1 text-sm text-gray-600">{displayValue}</div>
                                    </Tooltip>
                                ) : (
                                    <FormDisplayField
                                        fieldError={fieldError}
                                        value={displayValue}
                                        verificationResult={dataObject.verificationResults?.[field.fieldId]}
                                        {...field}
                                    />
                                );
                            },
                        }))) ||
                        []),
                ],
            }}
        >
            {dataObjectList && dataObjectList.length > 0 && (
                <SlideMenuGuide open={showMenuGuide} position={guidePostion} onClose={() => setShowMenuGuide(false)} zIndex={schemaFields?.length ?? 0} />
            )}
        </DataTable>
    );
};

export default DataObjectsPreviewList;
